import React from 'react'
import { Col, Row, Button, Card, CardBody, CardHeader } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import moment from 'moment-timezone'
import 'moment/locale/fi'
import { useNavigate, useParams } from 'react-router-dom'

import {
  SURVEY_STATE_TRANSLATIONS,
  SURVEY_STATE_COLORS,
} from '../../components/Inputs/schemas/survey'
import {
  CircularProgress,
  Typography,
  LinearProgress,
  Box,
  Badge,
  Chip,
} from '@mui/material'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { withAuthentication } from '../../components/Session'

function aggregateResults(survey, responses) {
  if (!survey?.inputs || !responses?.length) return []

  return survey.inputs.map((input, index) => {
    const questionResponses = responses
      .map((r) => r.answers?.[index.toString()])
      .filter((a) => a !== undefined && a !== null && a !== '')

    const result = {
      question: input.label,
      type: input.type,
      totalResponses: questionResponses.length,
      data: null,
    }

    if (input.type === 'rating' || input.type === 'number') {
      const numericValues = questionResponses.map(Number).filter((n) => !isNaN(n))
      if (numericValues.length > 0) {
        result.data = {
          average: (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2),
          min: Math.min(...numericValues),
          max: Math.max(...numericValues),
          count: numericValues.length,
        }
      }
    } else if (input.type === 'nps') {
      const numericValues = questionResponses.map(Number).filter((n) => !isNaN(n))
      if (numericValues.length > 0) {
        const promoters = numericValues.filter((n) => n >= 9).length
        const detractors = numericValues.filter((n) => n <= 6).length
        const total = numericValues.length
        const npsScore = Math.round(((promoters - detractors) / total) * 100)
        result.data = {
          nps: npsScore,
          promoters,
          passives: total - promoters - detractors,
          detractors,
          average: (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2),
          count: numericValues.length,
        }
      }
    } else if (['select', 'radio', 'multiselect', 'checkbox'].includes(input.type)) {
      const optionCounts = {}
      input.options?.forEach((opt) => {
        optionCounts[opt] = 0
      })
      questionResponses.forEach((response) => {
        if (Array.isArray(response)) {
          response.forEach((r) => {
            optionCounts[r] = (optionCounts[r] || 0) + 1
          })
        } else {
          optionCounts[response] = (optionCounts[response] || 0) + 1
        }
      })
      result.data = {
        options: optionCounts,
        total: questionResponses.length,
      }
    } else {
      result.data = {
        responses: questionResponses.slice(0, 10),
        total: questionResponses.length,
      }
    }

    return result
  })
}

function QuestionResult({ result }) {
  if (!result.data) {
    return (
      <Card className="mb-3">
        <CardBody>
          <Typography variant="subtitle1">{result.question}</Typography>
          <Typography variant="caption" color="textSecondary">
            Ei vastauksia
          </Typography>
        </CardBody>
      </Card>
    )
  }

  if (result.type === 'rating' || result.type === 'number') {
    return (
      <Card className="mb-3">
        <CardBody>
          <Typography variant="subtitle1">{result.question}</Typography>
          <Row className="mt-2">
            <Col xs={4}>
              <Typography variant="h4">{result.data.average}</Typography>
              <Typography variant="caption">Keskiarvo</Typography>
            </Col>
            <Col xs={4}>
              <Typography variant="h6">{result.data.min} - {result.data.max}</Typography>
              <Typography variant="caption">Min - Max</Typography>
            </Col>
            <Col xs={4}>
              <Typography variant="h6">{result.data.count}</Typography>
              <Typography variant="caption">Vastauksia</Typography>
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  }

  if (result.type === 'nps') {
    const npsColor = result.data.nps >= 50 ? 'success' : result.data.nps >= 0 ? 'warning' : 'error'
    return (
      <Card className="mb-3">
        <CardBody>
          <Typography variant="subtitle1">{result.question}</Typography>
          <Row className="mt-2">
            <Col xs={3}>
              <Typography variant="h3" color={npsColor}>
                {result.data.nps}
              </Typography>
              <Typography variant="caption">NPS</Typography>
            </Col>
            <Col xs={3}>
              <Typography variant="h6" color="success.main">{result.data.promoters}</Typography>
              <Typography variant="caption">Suosittelijat (9-10)</Typography>
            </Col>
            <Col xs={3}>
              <Typography variant="h6" color="text.secondary">{result.data.passives}</Typography>
              <Typography variant="caption">Passiiviset (7-8)</Typography>
            </Col>
            <Col xs={3}>
              <Typography variant="h6" color="error.main">{result.data.detractors}</Typography>
              <Typography variant="caption">Arvostelijat (0-6)</Typography>
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  }

  if (['select', 'radio', 'multiselect', 'checkbox'].includes(result.type)) {
    return (
      <Card className="mb-3">
        <CardBody>
          <Typography variant="subtitle1">{result.question}</Typography>
          <Typography variant="caption" className="mb-2">
            {result.data.total} vastausta
          </Typography>
          {Object.entries(result.data.options).map(([option, count]) => {
            const percentage = result.data.total > 0 ? (count / result.data.total) * 100 : 0
            return (
              <Box key={option} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{option}</Typography>
                  <Typography variant="body2">
                    {count} ({percentage.toFixed(0)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )
          })}
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <Typography variant="subtitle1">{result.question}</Typography>
        <Typography variant="caption" className="mb-2">
          {result.data.total} vastausta
        </Typography>
        {result.data.responses.map((response, i) => (
          <Box key={i} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">{response}</Typography>
          </Box>
        ))}
        {result.data.total > 10 && (
          <Typography variant="caption" color="textSecondary">
            ...ja {result.data.total - 10} muuta vastausta
          </Typography>
        )}
      </CardBody>
    </Card>
  )
}

class SurveyResultsContainerClass extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      survey: null,
      responses: [],
      aggregatedResults: [],
    }
  }

  componentDidMount() {
    this.loadData()
  }

  loadData = async () => {
    const { surveyId } = this.props
    this.setState({ loading: true })
    try {
      const [survey, responses] = await Promise.all([
        this.props.firebase.getSurvey(surveyId),
        this.props.firebase.getSurveyResponses(surveyId),
      ])
      const aggregatedResults = aggregateResults(survey, responses)
      this.setState({ survey, responses, aggregatedResults, loading: false })
    } catch (error) {
      console.error('Error loading survey results:', error)
      this.setState({ loading: false })
    }
  }

  handleBack = () => {
    this.props.navigate('/admin/messaging/surveys')
  }

  handleDeleteResponse = async (responseId) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän vastauksen?')) return
    try {
      await this.props.firebase.deleteSurveyResponse(this.props.surveyId, responseId)
      await this.loadData()
    } catch (error) {
      console.error('Error deleting response:', error)
    }
  }

  getResponseColumns = () => {
    const { survey } = this.state
    const columns = [
      {
        field: 'submittedAt',
        headerName: 'Lähetetty',
        width: 160,
        valueGetter: (value) => {
          if (value?.toDate) {
            return moment(value.toDate()).format('D.M.YYYY HH:mm')
          }
          return '-'
        },
      },
    ]

    if (!survey?.anonymous) {
      columns.push(
        {
          field: 'respondentName',
          headerName: 'Nimi',
          width: 150,
        },
        {
          field: 'respondentEmail',
          headerName: 'Sähköposti',
          width: 200,
        }
      )
    }

    survey?.inputs?.forEach((input, index) => {
      if (input.type !== 'paragraph') {
        columns.push({
          field: `answer_${index}`,
          headerName: input.label,
          width: 150,
          valueGetter: (value, row) => {
            const answer = row.answers?.[index.toString()]
            if (Array.isArray(answer)) {
              return answer.join(', ')
            }
            return answer || ''
          },
        })
      }
    })

    return columns
  }

  getResponseRows = () => {
    const { responses, survey } = this.state
    return responses.map((response) => {
      const row = {
        id: response.id,
        submittedAt: response.submittedAt,
        respondentName: response.respondentName || '',
        respondentEmail: response.respondentEmail || '',
        answers: response.answers,
      }
      survey?.inputs?.forEach((input, index) => {
        row[`answer_${index}`] = response.answers?.[index.toString()] || ''
      })
      return row
    })
  }

  render() {
    const { loading, survey, responses, aggregatedResults } = this.state

    if (loading) {
      return (
        <div className="content">
          <Row>
            <Col className="text-center">
              <CircularProgress />
              <Typography>Ladataan tuloksia...</Typography>
            </Col>
          </Row>
        </div>
      )
    }

    if (!survey) {
      return (
        <div className="content">
          <Row>
            <Col>
              <Button onClick={this.handleBack}>
                <ArrowBackIcon /> Takaisin
              </Button>
              <Typography className="mt-3">Kyselyä ei löytynyt.</Typography>
            </Col>
          </Row>
        </div>
      )
    }

    const stateColor = SURVEY_STATE_COLORS[survey.state] || 'secondary'
    const stateLabel = SURVEY_STATE_TRANSLATIONS[survey.state] || survey.state

    return (
      <div className="content">
        <Row>
          <Col>
            <Button onClick={this.handleBack}>
              <ArrowBackIcon /> Takaisin
            </Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Typography variant="h5">
              {survey.name}{' '}
              <Chip label={stateLabel} color={stateColor} size="small" />
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {survey.description}
            </Typography>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12} md={4}>
            <Card>
              <CardBody className="text-center">
                <Typography variant="h3">{responses.length}</Typography>
                <Typography variant="caption">Vastauksia yhteensä</Typography>
              </CardBody>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card>
              <CardBody className="text-center">
                <Typography variant="h3">{survey.inputs?.length || 0}</Typography>
                <Typography variant="caption">Kysymyksiä</Typography>
              </CardBody>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card>
              <CardBody className="text-center">
                <Typography variant="h6">
                  {survey.lastResponseAt?.toDate
                    ? moment(survey.lastResponseAt.toDate()).format('D.M.YYYY HH:mm')
                    : '-'}
                </Typography>
                <Typography variant="caption">Viimeisin vastaus</Typography>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs={12}>
            <Typography variant="h6">Tulosten yhteenveto</Typography>
          </Col>
        </Row>
        <Row className="mt-2">
          {aggregatedResults.map((result, index) => (
            <Col xs={12} md={6} key={index}>
              <QuestionResult result={result} />
            </Col>
          ))}
        </Row>

        <Row className="mt-4">
          <Col xs={12}>
            <Typography variant="h6">Yksittäiset vastaukset</Typography>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={12}>
            <Card>
              <CardBody style={{ height: 500 }}>
                <DataGrid
                  rows={this.getResponseRows()}
                  columns={this.getResponseColumns()}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    )
  }
}

function SurveyResultsContainer(props) {
  const navigate = useNavigate()
  const { surveyId } = useParams()
  return <SurveyResultsContainerClass {...props} navigate={navigate} surveyId={surveyId} />
}

export default withAuthentication(withFirebase(SurveyResultsContainer))
